import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  CircularProgress,
  Breadcrumbs,
  Link,
  Stack,
} from "@mui/material";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { getTenant } from "../../api/tenant";
import { APP_HOME_PATH } from "../../config/routes";

const TenantDetail = () => {
  const { id } = useParams();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        setLoading(true);
        const response = await getTenant(id);
        setTenant(response);
      } catch (error) {
        console.error("Failed to fetch tenant:", error);
        setTenant(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTenant();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tenant) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to={APP_HOME_PATH}
          >
            Dashboard
          </Link>
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to="/admin/tenants"
          >
            Tenants
          </Link>
          <Typography color="text.primary">Not found</Typography>
        </Breadcrumbs>
        <Typography color="error">Tenant not found</Typography>
        <Button sx={{ mt: 2 }} component={RouterLink} to="/admin/tenants">
          Back to tenant list
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
        <Link
          component={RouterLink}
          underline="hover"
          color="inherit"
          to={APP_HOME_PATH}
        >
          Dashboard
        </Link>
        <Link
          component={RouterLink}
          underline="hover"
          color="inherit"
          to="/admin/tenants"
        >
          Tenants
        </Link>
        <Typography color="text.primary">{tenant.name}</Typography>
      </Breadcrumbs>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 3 }}
      >
        <Typography variant="h4">Tenant details</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant="outlined" component={RouterLink} to="/admin/tenants">
            Back to tenant list
          </Button>
          {user?.roles?.includes("super_admin") && (
            <Button
              variant="contained"
              onClick={() => navigate(`/admin/tenants/${id}/edit`)}
            >
              Edit tenant
            </Button>
          )}
        </Stack>
      </Stack>

      <Paper elevation={0} variant="outlined" sx={{ p: 0 }}>
        <List disablePadding>
          <ListItem sx={{ py: 2, px: 3 }}>
            <ListItemText primary="ID" secondary={tenant.id} />
          </ListItem>
          <Divider component="li" />
          <ListItem sx={{ py: 2, px: 3 }}>
            <ListItemText primary="Organization name" secondary={tenant.name} />
          </ListItem>
          <Divider component="li" />
          <ListItem sx={{ py: 2, px: 3 }}>
            <ListItemText
              primary="Status"
              secondary={
                <Chip
                  label={tenant.is_active ? "Active" : "Inactive"}
                  color={tenant.is_active ? "success" : "error"}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              }
            />
          </ListItem>
          <Divider component="li" />
          <ListItem sx={{ py: 2, px: 3 }}>
            <ListItemText
              primary="Created"
              secondary={
                tenant.created_at
                  ? new Date(tenant.created_at).toLocaleString()
                  : "—"
              }
            />
          </ListItem>
          <Divider component="li" />
          <ListItem sx={{ py: 2, px: 3 }}>
            <ListItemText
              primary="Last updated"
              secondary={
                tenant.updated_at
                  ? new Date(tenant.updated_at).toLocaleString()
                  : "—"
              }
            />
          </ListItem>
        </List>
      </Paper>
    </Box>
  );
};

export default TenantDetail;
